import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { getCastingTagOptions, getStylingTagOptions } from '../../../api';

type TagEditorKind = 'casting' | 'styling';

interface CharacterTagEditorModalProps {
  open: boolean;
  kind: TagEditorKind;
  title: string;
  currentTags: string[];
  saving?: boolean;
  onSave: (tags: string[]) => void;
  onClose: () => void;
}

type TagOptions = Record<string, string[]>;

export default function CharacterTagEditorModal({
  open,
  kind,
  title,
  currentTags,
  saving = false,
  onSave,
  onClose,
}: CharacterTagEditorModalProps) {
  const { t } = useTranslation('projectDetail');
  const [options, setOptions] = useState<TagOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // 仅保留非空分类与非空标签（spec：渲染非空分类；全空时提示暂无可选标签）。
  const categories = useMemo<[string, string[]][]>(() => {
    if (!options) return [];
    return Object.entries(options)
      .map(([category, tags]) => [category, tags.filter(tag => tag && tag.trim() !== '')] as [string, string[]])
      .filter(([, tags]) => tags.length > 0);
  }, [options]);

  const orderedTags = useMemo(() => categories.flatMap(([, tags]) => tags), [categories]);

  const loadOptions = useCallback(() => {
    setLoading(true);
    setLoadError('');
    const fetcher = kind === 'casting' ? getCastingTagOptions : getStylingTagOptions;
    return fetcher()
      .then(data => setOptions(data))
      .catch((err: unknown) =>
        setLoadError(err instanceof Error && err.message ? err.message : 'load_failed'),
      )
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => {
    if (!open) return;
    // 每次打开建立独立草稿，后台刷新 currentTags 不覆盖本次编辑。
    // taxonomy 加载完成前禁止保存，未知标签由展示和保存时的 orderedTags 过滤。
    setOptions(null);
    setSelected(new Set(currentTags));
    void loadOptions();
    // currentTags 仅取打开时的快照；标签类型变化也会通过 loadOptions 开始新草稿。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadOptions]);

  const toggle = (tag: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  // 仅在 taxonomy 已加载、非加载中/非错误、且至少选一个时才允许保存。
  const canSave = !!options && !loading && !loadError && orderedTags.some(tag => selected.has(tag)) && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(orderedTags.filter(tag => selected.has(tag)));
  };

  const isEmpty = !loading && !loadError && options !== null && categories.length === 0;

  return (
    <Modal open={open} onClose={onClose} title={title} width="640px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', maxHeight: '60vh' }}>
        {loading ? (
          <div style={{ color: 'var(--text-2)', fontSize: '0.85rem' }}>{t('characterStyle.tagEditorLoading')}</div>
        ) : loadError ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', color: 'var(--danger)', fontSize: '0.85rem' }}>
            {t('characterStyle.tagEditorLoadFailed')}
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void loadOptions()}
              style={{ fontSize: '0.75rem' }}
            >
              {t('characterStyle.retry')}
            </button>
          </div>
        ) : isEmpty ? (
          <div style={{ color: 'var(--text-2)', fontSize: '0.85rem' }}>{t('characterStyle.tagEditorEmpty')}</div>
        ) : (
          <>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{t('characterStyle.tagEditorSelectHint')}</div>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', minHeight: 0 }}>
              {categories.map(([category, tags]) => (
                <div key={category} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{category}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
                    {tags.map(tag => {
                      const active = selected.has(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggle(tag)}
                          style={{
                            borderRadius: '12px',
                            padding: '3px 10px',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                            background: active ? 'var(--primary)' : 'var(--surface)',
                            color: active ? '#fff' : 'var(--text-2)',
                          }}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)' }}>
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={saving} style={{ fontSize: '0.78rem' }}>
            {t('characterStyle.cancelEdit')}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={!canSave}
            style={{ fontSize: '0.78rem' }}
          >
            {saving ? t('characterStyle.loading') : t('characterStyle.tagEditorSave')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
